import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { KeyRound } from "lucide-react";
import { api, ApiError } from "../../api/client";
import { Button } from "../../components/Button";
import { Dialog } from "../../components/Dialog";
import { Input } from "../../components/Input";
import { useToast } from "../../components/Toast";

// 修改密碼對話框。目前密碼 / 新密碼 / 再輸入一次新密碼。
//
// 三條規矩:
// 1. **兩次新密碼不一致就不送出。** 送出去的話後端會照收,使用者從此得用他打錯
//    的那一個登入,而他以為自己設的是另一個 —— 下一次就進不來了。
// 2. **失敗絕不關閉對話框。** 關掉等於把剛打的三個欄位全部丟掉,只能重打一次。
// 3. **密碼明文只出現在送出的 body 裡。** 不進錯誤訊息、不進成功提示、不進畫面,
//    與後端 app/routers/auth.py 的規矩同一條。

// 密碼規則:至少 6 個字元,且至少一個大寫、一個小寫英文字母(業主 2026-09-25)。
// 與後端 app/schemas.py 的 password_problem 同一套、同一句話。前端先擋只是為了
// 不必送出去才知道,真正的關卡在後端。

/** 密碼長度下限。與後端 app/schemas.py 的 MIN_PASSWORD_LENGTH 同一個數字。 */
const MIN_PASSWORD_LENGTH = 6;

/** 不合規則的統一說法。只講規則,不回吐使用者打了什麼。與後端一字不差。 */
export const PASSWORD_RULE = `密碼至少 ${MIN_PASSWORD_LENGTH} 個字元,且要有大寫與小寫英文字母`;

/** 新密碼欄下方的提示。 */
export const PASSWORD_HINT = `至少 ${MIN_PASSWORD_LENGTH} 個字元,要有大寫與小寫英文字母`;

/** 不合規則回 PASSWORD_RULE,合格回 null。英文字母只認 A-Z / a-z。 */
export function passwordProblem(password: string): string | null {
  if (password.length < MIN_PASSWORD_LENGTH || !/[A-Z]/.test(password) || !/[a-z]/.test(password)) {
    return PASSWORD_RULE;
  }
  return null;
}

export interface ChangePasswordDialogProps {
  open: boolean;
  onClose: () => void;
}

export function ChangePasswordDialog({ open, onClose }: ChangePasswordDialogProps) {
  const { push } = useToast();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // 每次開啟都從空白開始。上一次打的密碼沒有理由留在記憶體裡等下一次。
  useEffect(() => {
    if (!open) return;
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setError(null);
    setSubmitting(false);
  }, [open]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting) return;

    const problem = passwordProblem(newPassword);
    if (problem !== null) {
      // 訊息只講規則,不回吐使用者打了什麼。
      setError(problem);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("兩次輸入的新密碼不一致");
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await api.post<void>("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      push("密碼已更新,下次登入請用新的密碼。", "success");
      onClose();
    } catch (err) {
      // 後端的原句照抄。它刻意只說「目前密碼不正確」,前端不自己補細節。
      setError(err instanceof ApiError ? err.message : "無法連線到伺服器,請稍後再試。");
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="修改密碼"
      maxWidth="max-w-sm"
      // 打到一半誤點背景就整份消失太傷,關閉一律走 Esc 或明確的取消 / 關閉鈕。
      closeOnBackdrop={false}
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        <Input
          label="目前密碼"
          type="password"
          name="current_password"
          autoComplete="current-password"
          autoFocus
          required
          value={currentPassword}
          onChange={(event) => setCurrentPassword(event.target.value)}
        />
        <Input
          label="新密碼"
          type="password"
          name="new_password"
          autoComplete="new-password"
          required
          hint={PASSWORD_HINT}
          value={newPassword}
          onChange={(event) => setNewPassword(event.target.value)}
        />
        <Input
          label="再輸入一次新密碼"
          type="password"
          name="confirm_password"
          autoComplete="new-password"
          required
          value={confirmPassword}
          onChange={(event) => setConfirmPassword(event.target.value)}
        />

        {error ? (
          <p
            role="alert"
            className="m-0 rounded-[var(--radius)] border border-[var(--danger)] bg-[var(--surface-2)] px-2.5 py-2 text-sm text-[var(--danger)]"
          >
            {error}
          </p>
        ) : null}

        <div className="mt-1 flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            取消
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={submitting}
            icon={<KeyRound aria-hidden="true" size={16} />}
          >
            更新密碼
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
